import React, {useState} from 'react'
import PropTypes from 'prop-types'
import ContentBox from '../../../layout/component/ContentBox'
import Row from '../../../bootstrap/component/Row'
import Column from '../../../bootstrap/component/Column'
import Select from '../../../form/component/Select'
import LabelInput from '../../../form/component/LabelInput'
import ValidationMessageList from '../../../validation/component/ValidationMessageList'
import currentUserHasAccess from '../../../../util/widget-helper'
import { PHYSICIAN_AND_SUPPLIER_WIDGET } from '../../../process/ui-definition/process-ui'
import {
    ROLE_PARTNER_PHYSICIAN_REMOTE,
    ROLE_TREATING_PHYSICIAN_REMOTE,
} from '../../../security/state/auth/const'
import store from '../../../../modules/store'
import { selectSupplierDefaultId } from '../../../globals/state/selector'


const ExternalPhysicianSelect = ({ customer, physicianList, changeCustomer, readOnly }) => {
    if (customer.treatingPhysicianId) {
        return null
    }

    return (
        <LabelInput label="Partner Physician (External)">
            <Select object={customer}
                name="partnerPhysicianId"
                valueList={physicianList}
                addEmpty={!customer.partnerPhysicianId}
                renderer={(physician) => {
                    return physician.displayName
                }}
                readOnly={readOnly}
                onChange={(name, value) => {
                    changeCustomer(name, value)
                }}/>
        </LabelInput>
    )
}

const RemotePhysicianSelect = ({ customer, userList, changeCustomer, readOnly }) => {
    const remotePhysicianList = RemotePhysicianList(userList)
    const isRemotePhysician = IsRemotePhysician(remotePhysicianList, customer)

    if (customer.treatingPhysicianId && !isRemotePhysician) {
        return null
    }

    return (
        <LabelInput label="Treating Physician (Remote)">
            <Select object={customer}
                id="treatingPhysicianId"
                name="treatingPhysicianId"
                valueList={remotePhysicianList}
                addEmpty={!customer.treatingPhysicianId}
                renderer={(user) => {
                    return user.firstName + ' ' + user.lastName
                }}
                readOnly={readOnly}
                onChange={(name, value) => {
                    changeCustomer('partnerPhysicianId', null)
                    changeCustomer(name, value)
                }}
            />
        </LabelInput>
    )
}

const RemotePhysicianList = (userList) => {
    const remotePhysicianList = []
    userList.forEach(user => {
        const roleList = user.roleList
        if (roleList.includes(ROLE_TREATING_PHYSICIAN_REMOTE) || roleList.includes(ROLE_PARTNER_PHYSICIAN_REMOTE)) {
            remotePhysicianList.push(user)
        }
    }, userList)

    return remotePhysicianList
}

const IsRemotePhysician = (remotePhysicianList, customer) => {
    let isRemotePhysician = false
    const treatingPhysicianId = customer.treatingPhysicianId
    remotePhysicianList.forEach(user => {
        if (user.id === treatingPhysicianId) {
            if (user.store) {
                isRemotePhysician = user.store !== customer.store
            } else {
                isRemotePhysician = true
            }
        }
    }, remotePhysicianList)

    return isRemotePhysician
}

const SunshinePhysicianSelect = ({ customer, userList }) => {
    if (!customer.treatingPhysicianId) {
        return null
    }

    const remotePhysicianList = RemotePhysicianList(userList)
    const isRemotePhysician = IsRemotePhysician(remotePhysicianList, customer)

    if (isRemotePhysician) {
        return null
    }

    return (
        <LabelInput label="Treating Physician">
            <Select object={customer}
                name="treatingPhysicianId"
                valueList={userList}
                addEmpty={false}
                renderer={(user) => {
                    return user.firstName + ' ' + user.lastName
                }}
                readOnly={true}
                onChange={() => {
                }}
            />
        </LabelInput>
    )
}


const PhysicianAndSupplier = ({ customer, changeCustomer, saveCustomer, physicianList, treatmentPlanerList, supplierList, validationMessageList, userList, isExternalTreatmentPlanerPossible }) => {
    if (!currentUserHasAccess(PHYSICIAN_AND_SUPPLIER_WIDGET)) {
        return null
    }

    const defaultSupplierId = selectSupplierDefaultId(store.getState())

    const [readOnly, setReadOnly] = useState(true);

    const [isVisible, setIsVisible] = useState(false);

    // set customer supplier Id to default if undefined so validation does not complain
    if (customer.partnerSupplierId === undefined || !customer.partnerSupplierId) {
        changeCustomer('partnerSupplierId', defaultSupplierId)
        const remotePhysicianList = RemotePhysicianList(userList)
        const isRemotePhysician = IsRemotePhysician(remotePhysicianList, customer)
        const treatingPhysicianIdSelect = document.getElementById('treatingPhysicianId')
        if (isRemotePhysician && treatingPhysicianIdSelect) {
            const treatingPhysicianId = treatingPhysicianIdSelect.value
            changeCustomer('treatingPhysicianId', treatingPhysicianId)
        }
        saveCustomer()
    }

    const toggleEditButtons = () => {
        setReadOnly(!readOnly)
        setIsVisible(!isVisible)
    }

    const ButtonGroup = () => {

        const btnStyle = {
            marginRight: '10px'
        }

        const onCancel = () => {
            toggleEditButtons()
        }

        const onSave = () => {
            saveCustomer()
            toggleEditButtons()
        }

        return (
            <React.Fragment>
                <button
                    className="btn btn-primary pull-right"
                    onClick={onSave}>
                    Save
                </button>
                <button
                    className="btn btn-default pull-right"
                    style={btnStyle}
                    onClick={onCancel}>
                    Cancel
                </button>
            </React.Fragment>
        )
    }

    return (
        <ContentBox title="Physician and Supplier">
            <Row>
                <Column size={12}>
                    {!isVisible &&
                        <button
                            className="btn btn-primary pull-right"
                            onClick={toggleEditButtons}>
                            <span className="fa fa-edit" aria-hidden="true"/>
                        </button>
                    }
                    {isVisible &&
                        <ButtonGroup/>
                    }
                </Column>
            </Row>
            <Row>
                <Column size={12}>
                    <ValidationMessageList validationMessageList={validationMessageList}/>
                </Column>
            </Row>
            <Row>
                <Column size={4}>
                    <ExternalPhysicianSelect
                        readOnly={readOnly}
                        customer={customer}
                        physicianList={physicianList}
                        changeCustomer={changeCustomer}
                    />
                    <RemotePhysicianSelect
                        readOnly={readOnly}
                        customer={customer}
                        userList={userList}
                        changeCustomer={changeCustomer}
                    />
                    <SunshinePhysicianSelect customer={customer} userList={userList}/>
                </Column>
                <Column size={4}>
                    <LabelInput label="Supplier">
                        <Select object={customer}
                            name="partnerSupplierId"
                            valueList={supplierList}
                            addEmpty={!customer.partnerSupplierId}
                            renderer={(supplier) => {
                                return supplier.displayName
                            }}
                            readOnly={readOnly}
                            onChange={(name, value) => {
                                changeCustomer('partnerTreatmentPlanerId', null)
                                changeCustomer(name, value)
                            }}
                            defaultValue={defaultSupplierId}
                        />
                    </LabelInput>
                </Column>
                <Column size={4}>
                    {isExternalTreatmentPlanerPossible && (
                        <LabelInput label="External Treatment Planer">
                            <Select object={customer}
                                name="partnerTreatmentPlanerId"
                                valueList={treatmentPlanerList}
                                addEmpty={true}
                                renderer={(supplier) => {
                                    return supplier.displayName
                                }}
                                readOnly={readOnly}
                                onChange={(name, value) => {
                                    changeCustomer(name, value)
                                }}/>
                        </LabelInput>
                    )}
                </Column>
            </Row>
        </ContentBox>
    )
}

PhysicianAndSupplier.propTypes = {
    readOnly: PropTypes.bool,
    customer: PropTypes.object.isRequired,
    changeCustomer: PropTypes.func.isRequired,
    saveCustomer: PropTypes.func.isRequired,
    physicianList: PropTypes.array.isRequired,
    treatmentPlanerList: PropTypes.array.isRequired,
    userList: PropTypes.array,
    supplierList: PropTypes.array.isRequired,
    validationMessageList: PropTypes.array,
    isExternalTreatmentPlanerPossible: PropTypes.bool,
}

export default PhysicianAndSupplier



// WEBPACK FOOTER //
// ./src/modules/customer/component/detail/PhysicianAndSupplier.js